import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { NavigationStart, Router } from '@angular/router';
import { EMPTY, Observable, expand, filter, forkJoin, map, of, reduce, shareReplay, switchMap, tap } from 'rxjs';
import { environment } from '../../environments/environment';
import { Page } from '../models/page.model';
import {
  CreateEntryRequest,
  CreateObjectMappingRequest,
  EntryState,
  ObjectMappingEntryView,
  ObjectMappingSearchResult,
  ObjectMappingSummary,
  ObjectUsages,
  RenameResult,
  UpdateEntryRequest,
} from '../models/object-mapping.model';

/** Groesste Seite, die das BE annimmt (`spring.data.web.pageable.max-page-size`). */
const LIST_PAGE_SIZE = 100;

/** Vorschlaege im TCDL-Editor: nie mehr als eine Liste voll laden. */
const SUGGESTION_LIMIT = 20;
const SUGGESTION_CACHE_MS = 30_000;

function uniqueSorted(names: string[]): string[] {
  return [...new Set(names)].sort((a, b) => a.localeCompare(b));
}

@Injectable({ providedIn: 'root' })
export class ObjectMappingService {
  private readonly http = inject(HttpClient);
  private readonly base = `${environment.apiBaseUrl}/api/v1/projects`;
  private readonly suggestionCache = new Map<string, { until: number; answer: Observable<unknown> }>();
  /** Libraries checked by the TCDL editor, kept while the page is open. */
  private readonly libraryCache = new Map<string, Observable<ReadonlySet<string> | null>>();

  constructor() {
    inject(Router)
      .events.pipe(filter((event) => event instanceof NavigationStart))
      .subscribe(() => this.libraryCache.clear());
  }

  list(projectId: string, page: number, size: number): Observable<Page<ObjectMappingSummary>> {
    const params = new HttpParams().set('page', String(page)).set('size', String(size));
    return this.http.get<Page<ObjectMappingSummary>>(`${this.base}/${projectId}/object-mappings`, { params });
  }

  /** Alle Bibliotheken des Projekts; das BE liefert sie nur seitenweise. */
  listAll(projectId: string): Observable<ObjectMappingSummary[]> {
    return this.list(projectId, 0, LIST_PAGE_SIZE).pipe(
      expand((page) => (page.last ? EMPTY : this.list(projectId, page.number + 1, LIST_PAGE_SIZE))),
      map((page) => page.content),
      reduce((all, content) => [...all, ...content], [] as ObjectMappingSummary[]),
    );
  }

  entries(
    projectId: string,
    mappingId: string,
    q: string,
    page: number,
    size: number,
    state: EntryState | null = null,
  ): Observable<Page<ObjectMappingEntryView>> {
    let params = new HttpParams().set('page', String(page)).set('size', String(size));
    const term = q.trim();
    if (term) params = params.set('q', term);
    if (state) params = params.set('state', state);
    return this.http.get<Page<ObjectMappingEntryView>>(
      `${this.base}/${projectId}/object-mappings/${mappingId}/entries`,
      { params },
    );
  }

  /** Namen fuer den TCDL-Editor: ohne Text die ersten Bibliotheken, sonst die passenden. */
  libraryNames(projectId: string, prefix: string): Observable<string[]> {
    if (!prefix.trim()) {
      const params = new HttpParams().set('page', '0').set('size', String(SUGGESTION_LIMIT));
      return this.cachedGet<Page<ObjectMappingSummary>>(`${this.base}/${projectId}/object-mappings`, params).pipe(
        map((page) => uniqueSorted(page.content.map((m) => m.name))),
      );
    }
    return this.search(projectId, prefix).pipe(map((result) => uniqueSorted(result.mappings.map((m) => m.name))));
  }

  /**
   * Eintraege der Bibliothek mit diesem Namen. Tragen mehrere Bibliotheken ihn (andere Engine),
   * kommen ihre Eintraege zusammen; `/search` selbst sucht Eintraege projektweit.
   */
  entryNames(projectId: string, library: string, prefix: string): Observable<string[]> {
    const wanted = library.toLowerCase();
    return this.search(projectId, library).pipe(
      map((result) => result.mappings.filter((m) => m.name.toLowerCase() === wanted)),
      switchMap((mappings) =>
        mappings.length
          ? forkJoin(mappings.map((m) => this.cachedEntries(projectId, m.id, prefix)))
          : of([] as ObjectMappingEntryView[][]),
      ),
      map((pages) => uniqueSorted(pages.flat().map((e) => e.logicalName))),
    );
  }

  /**
   * Alle Eintragsnamen der Bibliotheken mit genau diesem Namen, `null` wenn es keine gibt.
   * Jede Bibliothek wird je Seite einmal geladen; eine fehlgeschlagene Anfrage wird nicht behalten.
   */
  libraryEntries(projectId: string, library: string): Observable<ReadonlySet<string> | null> {
    const key = `${projectId}\u0000${library}`;
    const cached = this.libraryCache.get(key);
    if (cached) return cached;
    const params = new HttpParams().set('q', library).set('limit', String(SUGGESTION_LIMIT));
    const answer = this.http
      .get<ObjectMappingSearchResult>(`${this.base}/${projectId}/object-mappings/search`, { params })
      .pipe(
        map((result) => result.mappings.filter((m) => m.name === library)),
        switchMap((mappings) =>
          mappings.length ? forkJoin(mappings.map((m) => this.allEntries(projectId, m.id))) : of(null),
        ),
        map((lists) => (lists ? new Set(lists.flat().map((e) => e.logicalName)) : null)),
        tap({ error: () => this.libraryCache.delete(key) }),
        shareReplay({ bufferSize: 1, refCount: true }),
      );
    this.libraryCache.set(key, answer);
    return answer;
  }

  /** Verwender einer Bibliothek oder, mit `entryId`, eines einzelnen Eintrags. */
  usages(projectId: string, mappingId: string, entryId?: string): Observable<ObjectUsages> {
    return this.http.get<ObjectUsages>(`${this.target(projectId, mappingId, entryId)}/usages`);
  }

  /** Mit `preview` nur die Vorschau; das BE schreibt erst bei `preview=false`. */
  rename(
    projectId: string,
    mappingId: string,
    entryId: string | undefined,
    newName: string,
    preview: boolean,
  ): Observable<RenameResult> {
    const params = new HttpParams().set('preview', String(preview));
    return this.http.post<RenameResult>(
      `${this.target(projectId, mappingId, entryId)}/rename`,
      { newName },
      { params },
    );
  }

  create(projectId: string, body: CreateObjectMappingRequest): Observable<ObjectMappingSummary> {
    return this.http
      .post<ObjectMappingSummary>(`${this.base}/${projectId}/object-mappings`, body)
      .pipe(tap(() => this.suggestionCache.clear()));
  }

  /** Ohne `confirm` antwortet das BE bei Verwendern mit 409 und `ObjectUsages` als Body. */
  deleteMapping(projectId: string, mappingId: string, confirm: boolean): Observable<void> {
    const params = new HttpParams().set('confirm', String(confirm));
    return this.http
      .delete<void>(this.target(projectId, mappingId), { params })
      .pipe(tap(() => this.suggestionCache.clear()));
  }

  createEntry(projectId: string, mappingId: string, body: CreateEntryRequest): Observable<ObjectMappingEntryView> {
    return this.http
      .post<ObjectMappingEntryView>(`${this.target(projectId, mappingId)}/entries`, body)
      .pipe(tap(() => this.suggestionCache.clear()));
  }

  updateEntry(
    projectId: string,
    mappingId: string,
    entryId: string,
    body: UpdateEntryRequest,
  ): Observable<ObjectMappingEntryView> {
    return this.http.put<ObjectMappingEntryView>(this.target(projectId, mappingId, entryId), body);
  }

  /** Ohne `confirm` antwortet das BE bei Verwendern mit 409 und `ObjectUsages` als Body. */
  deleteEntry(projectId: string, mappingId: string, entryId: string, confirm: boolean): Observable<void> {
    const params = new HttpParams().set('confirm', String(confirm));
    return this.http.delete<void>(this.target(projectId, mappingId, entryId), { params });
  }

  private search(projectId: string, q: string): Observable<ObjectMappingSearchResult> {
    const params = new HttpParams().set('q', q.trim()).set('limit', String(SUGGESTION_LIMIT));
    return this.cachedGet(`${this.base}/${projectId}/object-mappings/search`, params);
  }

  private allEntries(projectId: string, mappingId: string): Observable<ObjectMappingEntryView[]> {
    return this.entries(projectId, mappingId, '', 0, LIST_PAGE_SIZE).pipe(
      expand((page) => (page.last ? EMPTY : this.entries(projectId, mappingId, '', page.number + 1, LIST_PAGE_SIZE))),
      reduce((all, page) => [...all, ...page.content], [] as ObjectMappingEntryView[]),
    );
  }

  private cachedEntries(projectId: string, mappingId: string, q: string): Observable<ObjectMappingEntryView[]> {
    let params = new HttpParams().set('page', '0').set('size', String(SUGGESTION_LIMIT));
    if (q.trim()) params = params.set('q', q.trim());
    return this.cachedGet<Page<ObjectMappingEntryView>>(
      `${this.base}/${projectId}/object-mappings/${mappingId}/entries`,
      params,
    ).pipe(map((page) => page.content));
  }

  /** Gleiche Anfrage binnen 30 s aus dem Speicher; eine fehlgeschlagene wird nicht behalten. */
  private cachedGet<T>(url: string, params: HttpParams): Observable<T> {
    const key = `${url}?${params.toString()}`;
    const now = Date.now();
    const hit = this.suggestionCache.get(key);
    if (hit && hit.until > now) return hit.answer as Observable<T>;
    for (const [old, cached] of this.suggestionCache) if (cached.until <= now) this.suggestionCache.delete(old);
    const answer = this.http
      .get<T>(url, { params })
      .pipe(tap({ error: () => this.suggestionCache.delete(key) }), shareReplay({ bufferSize: 1, refCount: true }));
    this.suggestionCache.set(key, { until: now + SUGGESTION_CACHE_MS, answer });
    return answer;
  }

  private target(projectId: string, mappingId: string, entryId?: string): string {
    const mapping = `${this.base}/${projectId}/object-mappings/${mappingId}`;
    return entryId ? `${mapping}/entries/${entryId}` : mapping;
  }
}
